import React, {useState} from 'react';
import {GitMerge, CheckCircle2, Loader2} from 'lucide-react';
import {ApiService} from '../services/apiService';
import {Button} from './ui/Button';
import {Field} from './ui/Input';

interface RegisterProps {
    onSwitchToLogin: () => void;
}

export const Register: React.FC<RegisterProps> = ({onSwitchToLogin}) => {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            const apiService = ApiService.fromSession(import.meta.env.VITE_API_BASE_URL || '/api');
            await apiService.register(email, password, fullName);
            setIsSuccess(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create account');
        } finally {
            setIsLoading(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#f4f1e6] p-4 text-[#0a0a0a]">
                <div className="w-full max-w-md text-center">
                    <div className="nb-shadow-lg rounded-2xl border-2 border-[#0a0a0a] bg-white p-8">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#0a0a0a] bg-[#bbf7d0] text-[#15803d]">
                            <CheckCircle2 size={32} strokeWidth={3}/>
                        </div>
                        <h2 className="mb-2 text-2xl font-extrabold">Check your inbox</h2>
                        <p className="mb-6 font-medium text-[#4f4f48]">
                            We&apos;ve sent an activation link to your email address. Please click the
                            link to activate your account.
                        </p>
                        <Button variant="secondary" onClick={onSwitchToLogin} className="w-full">
                            Back to Log In
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#f4f1e6] p-4 text-[#0a0a0a]">
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <div className="mb-4 inline-flex items-center gap-3">
                        <div className="nb-shadow-sm rounded-2xl border-2 border-[#0a0a0a] bg-[#22c55e] p-3">
                            <GitMerge size={32} className="text-[#0a0a0a]" strokeWidth={2.6}/>
                        </div>
                        <h1 className="text-4xl font-extrabold tracking-tight">ReviewMe</h1>
                    </div>
                    <p className="text-lg font-medium text-[#4f4f48]">Create your account</p>
                </div>

                <div className="nb-shadow-lg rounded-2xl border-2 border-[#0a0a0a] bg-white p-8">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <div className="rounded-xl border-2 border-[#0a0a0a] bg-[#fecdd3] p-3 text-sm font-bold text-[#be123c]">
                                {error}
                            </div>
                        )}
                        <Field
                            label="Full Name"
                            type="text"
                            required
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            disabled={isLoading}
                        />
                        <Field
                            label="Email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isLoading}
                        />
                        <Field
                            label="Password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={isLoading}
                        />
                        <Button type="submit" disabled={isLoading} className="mt-2 w-full text-base">
                            {isLoading ? (
                                <>
                                    <Loader2 size={20} className="animate-spin"/>
                                    Creating Account...
                                </>
                            ) : (
                                'Create Account'
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="font-medium text-[#4f4f48]">
                            Already have an account?{' '}
                            <button onClick={onSwitchToLogin} className="font-extrabold text-[#15803d] hover:underline">
                                Log In
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
